"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import type { LfgLftPostResponse } from "@/shared/api/lfg-lft-types";
import {
  LFG_FORMATS,
  LFG_LANGUAGES,
  LFG_ROLES,
  LFG_SERVERS,
  isLfgRole,
  toLfgRank,
  utcMaskToLocalRanges,
  type LfgRole,
} from "../model/board-filters";
import { CardChip, LfgRoleIcon, MicIcon, RankLabel } from "./board-card-parts";

const pad = (h: number) => String(h).padStart(2, "0");

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[62px_1fr] items-start gap-2">
      <dt className="text-xs leading-[22px] text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 flex-wrap gap-1">{children}</dd>
    </div>
  );
}

/**
 * «Требования» объявления: что ищут. У команды роль — её свободные искомые позиции,
 * у игрока — его роли. Неуказанное — «Не важно», а не пустая строка.
 */
export function BoardRequirements({ post, tzOffset }: { post: LfgLftPostResponse; tzOffset: number }) {
  const t = useTranslations("LfgBoard");
  const any = <CardChip className="text-muted-foreground">{t("card.notImportant")}</CardChip>;

  const roles: LfgRole[] = (post.roles ?? [post.role]).filter(isLfgRole);
  const ordered = LFG_ROLES.filter((r) => roles.includes(r));

  const hasRank = !!(post.minRank || post.maxRank);
  const from = toLfgRank(post.minRank);
  const to = post.maxRank ? toLfgRank(post.maxRank) : "Challenger";

  const hours = post.hoursUtcMask ? utcMaskToLocalRanges(post.hoursUtcMask, tzOffset) : [];
  const servers = post.servers ?? [];
  const formats = post.searchPurposes ?? [];
  const languages = post.languages ?? [];

  return (
    <section aria-label={t("card.requirements")} className="flex flex-col gap-2.5">
      <h3 className="text-[11px] font-bold uppercase tracking-[1.2px] text-muted-foreground">{t("card.requirements")}</h3>
      <dl className="flex flex-col gap-[7px]">
        <Row label={t("card.reqRole")}>
          {ordered.length === 0 ? (
            <CardChip>{t("card.anyRole")}</CardChip>
          ) : (
            ordered.map((r) => (
              <CardChip key={r}>
                {t(`role.${r}`)}
                <LfgRoleIcon role={r} />
              </CardChip>
            ))
          )}
        </Row>

        {post.type === "Lft" && (
          <Row label={t("card.reqRank")}>
            {hasRank ? (
              <CardChip className="h-auto min-h-[22px] flex-wrap py-0.5">
                <RankLabel rank={from} size={18} />
                {from !== to && (
                  <>
                    <span aria-hidden className="text-muted-foreground">→</span>
                    <RankLabel rank={to} size={18} />
                  </>
                )}
              </CardChip>
            ) : (
              <CardChip>{t("card.anyRank")}</CardChip>
            )}
          </Row>
        )}

        <Row label={t("card.reqMic")}>
          {post.mic === "With" ? (
            <MicIcon mic="With" title={t("card.micRequired")} />
          ) : post.mic === "Without" ? (
            <MicIcon mic="Without" title={t("card.micNotNeeded")} />
          ) : (
            any
          )}
        </Row>

        <Row label={t("card.reqHours")}>
          {hours === null ? (
            <CardChip>{t("card.allDay")}</CardChip>
          ) : hours.length === 0 ? (
            any
          ) : (
            hours.map(([a, b]) => <CardChip key={a}>{t("hoursRange", { from: pad(a), to: pad(b) })}</CardChip>)
          )}
        </Row>

        <Row label={t("card.reqServer")}>
          {servers.length === 0 ? any : servers.length === LFG_SERVERS.length ? (
            <CardChip>{t("card.all")}</CardChip>
          ) : (
            servers.map((s) => <CardChip key={s}>{s}</CardChip>)
          )}
        </Row>

        <Row label={t("card.reqFormat")}>
          {formats.length === 0 ? any : formats.length === LFG_FORMATS.length ? (
            <CardChip>{t("card.all")}</CardChip>
          ) : (
            LFG_FORMATS.filter((f) => formats.includes(f)).map((f) => <CardChip key={f}>{t(`format.${f}`)}</CardChip>)
          )}
        </Row>

        <Row label={t("card.reqLanguage")}>
          {languages.length === 0
            ? any
            : LFG_LANGUAGES.filter((l) => languages.includes(l)).map((l) => <CardChip key={l}>{t(`language.${l}`)}</CardChip>)}
        </Row>
      </dl>
    </section>
  );
}
